"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  title: "Financieel inzicht",
  description: "Grip op cijfers, marges en rendement",
  link: "/Finance",
  image: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/025_StephenVBFotografie_delichttoren_20231026-scaled.jpg?v=y0b8zz8kx8d",
  srcset: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/025_StephenVBFotografie_delichttoren_20231026-scaled.jpg?v=kwxkp3f5pl-wvc-srcset",
  sizes: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/025_StephenVBFotografie_delichttoren_20231026-scaled.jpg?v=w1mlueoc7u8-wvc-sizes",
  bullets: ["Marge- en kostenoptimalisatie", "Financiële prognoses en sturing", "Optimalisatie van (inkoop)-contracten"]
}, {
  title: "Operationeel",
  description: "Slimmere processen en hogere marges",
  link: "/Operations",
  image: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/header-foto-scaled.jpg?v=qga6jxhee2f",
  srcset: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/header-foto-scaled.jpg?v=9uw9mzlldeh-wvc-srcset",
  sizes: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/header-foto-scaled.jpg?v=afmpf4pprg5-wvc-sizes",
  bullets: ["Efficiëntere routing en werkprocessen", "Grip op inkoop en kostprijzen", "Voorraad- en dervingscontrole"]
}, {
  title: "Personeelszaken",
  description: "Meer grip op personeel en loonkosten",
  link: "/hr-personeelsbeheer",
  image: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/De-Vaste-Clique-7-scaled.jpg?v=0ika5b12d804",
  srcset: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/HR-.jpg?v=ffcyz8yf0uq-wvc-srcset",
  sizes: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/HR-.jpg?v=i6fec9xwg8-wvc-sizes",
  bullets: ["Contractbeheer en verloning", "Sturing op loonkosten", "Structuur in HR-processen"]
}, {
  title: "Marketing",
  description: "Meer zichtbaarheid, gasten en omzet",
  link: "/Marketing",
  image: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/Fotos-Lichttoren-18-04-2023-24-scaled.jpg?v=c8igdiq0e7",
  srcset: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/Fotos-Lichttoren-18-04-2023-24-scaled.jpg?v=hv6itsgolo-wvc-srcset",
  sizes: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/Fotos-Lichttoren-18-04-2023-24-scaled.jpg?v=4ct80rzmz5h-wvc-sizes",
  bullets: ["Social media beheer", "Professionele contentcreatie", "Actiegerichte campagnes"]
}, {
  title: "Administratie",
  description: "Rust, overzicht en betrouwbare cijfers",
  link: "/Administratie",
  image: "https://goodvibeshospitality.nl/wp-content/uploads/2026/05/De-Vaste-Clique-68-scaled.jpg?v=ad9z2n9nscu",
  srcset: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/De-Vaste-Clique-7-scaled.jpg?v=uqxw7rlx5a-wvc-srcset",
  sizes: "https://accurate-cicada.10web.cloud/wp-content/uploads/2026/05/De-Vaste-Clique-7-scaled.jpg?v=2fryyjlelcs-wvc-sizes",
  bullets: ["Factuurverwerking", "Omzetcontrole en kassakoppelingen", "BTW-aangiftes en jaarrekening"]
}];
interface ServiceCardProps {
  service: typeof services[0];
  index: number;
}
function ServiceCard({
  service,
  index
}: ServiceCardProps) {
  const {
    ref: cardRef,
    isVisible: isCardVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div key={index} data-index={index} ref={cardRef} className={`transition-all duration-700 ${isCardVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="bg-white border-none flex h-full flex-col transition-all duration-300 shadow-md overflow-hidden gap-8">
        <div className="relative h-24 md:h-36 w-full overflow-hidden">
          <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.title} loading="lazy" className="w-full object-cover transition-transform duration-500 group-hover:scale-105 !p-0 !h-[150px]" />
        </div>
        <CardHeader className="@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 ![padding:0px_24px_0px_24px] pb-2">
          <CardTitle className="font-default font-semibold text-foreground !text-lg">
            {service.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-grow space-y-6">
          <CardDescription className="text-muted-foreground font-default leading-relaxed !text-sm">
            {service.description}
          </CardDescription>
          {service.bullets.length > 0 && <ul className="space-y-3">
              {service.bullets.map((bullet, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="text-primary size-4 shrink-0 mt-0.5" />
                  <span>{bullet}</span>
                </li>)}
            </ul>}
        </CardContent>
        
      </Card>
    </div>;
}
export default function RelatedServicesGrid() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="260727" id="pillars-of-excellence" className="bg-background relative overflow-hidden ![padding:30px_0px_96px_0px]">
      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`mb-12 text-center transition-all duration-700 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <span className="text-primary mb-2 block text-xs font-bold tracking-[0.2em] uppercase">alles onder een dak. Alles gericht op jouw resultaat.</span>
          <div className="w-8 h-px bg-primary/30 mx-auto mb-4" />
          
          <h2 className="font-default text-foreground text-3xl font-bold md:text-4xl lg:text-5xl mb-4">Een team. Vijf expertises.</h2>
          
          
          <div className="w-12 h-1 bg-primary mx-auto mb-8" />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service, i) => <ServiceCard key={i} service={service} index={i} />)}
        </div>
      </div>
    </section>;
}